import { useMemo } from "react";

import { UserPermissions } from "@/features/users/hooks/types";
import {
  Capabilities,
  OrganizationRoles,
  Roles,
} from "@/shared/utils/constants";
import { BaseUserResponse, FullUserResponse } from "@/redux/users/types";
import { hasCapability } from "@/shared/utils/roles";

export const useUserPermissions = (
  user: BaseUserResponse | FullUserResponse | null | undefined,
  currentUser: FullUserResponse | null | undefined,
): UserPermissions => {
  return useMemo(() => {
    if (!currentUser || !user) {
      return {
        canEdit: false,
        canViewOverviewTab: true,
        canViewEvidenceTab: false,
        canViewDiscussionsTab: false,
        canViewCommentsTab: false,
        canViewProfileFormsTab: true,
        canImpersonate: false,
        canCopyResetPasswordLink: false,
      };
    }

    const isCurrentUserGuest =
      currentUser.organizationRole === OrganizationRoles.GUEST.value;
    const canAccessEvidence = hasCapability(
      currentUser,
      Capabilities.ACCESS_EVIDENCE,
    );
    const canAccessCommunity = hasCapability(
      currentUser,
      Capabilities.ACCESS_COMMUNITY,
    );
    const isProfileOwner = currentUser.id === user.id;
    const isCurrentUserAppAdmin = currentUser.role === Roles.ADMIN.value;
    const isAppAdmin = currentUser.role === Roles.ADMIN.value;

    return {
      canEdit: isProfileOwner || isCurrentUserAppAdmin,
      canViewOverviewTab: true,
      canViewEvidenceTab:
        canAccessEvidence && (!isCurrentUserGuest || isProfileOwner),
      canViewDiscussionsTab: canAccessCommunity,
      canViewCommentsTab: canAccessCommunity,
      canViewProfileFormsTab: true,
      canImpersonate: isAppAdmin,
      canCopyResetPasswordLink: isAppAdmin,
    };
  }, [user, currentUser]);
};
